import type React from 'react';

import { Button, Label, Typography } from '@packages/ui/shared';
import Select from '@web/components/select';
import { useCallback, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';

/** Inline style for the confirm button left margin. */
const confirmButtonStyle = { marginLeft: 15 };

/**
 * Props for the {@link AddWorksheetModal} component.
 */
type AddWorksheetModalProps = {
    /** Available worksheet group options for the group dropdown. */
    groupOptions?: {
        /** The option identifier. */
        value: string;
        /** The option display text. */
        title: string;
    }[];

    /** Whether options are currently being fetched. */
    isLoading?: boolean;

    /** Callback invoked when the user confirms a group + worksheet selection. */
    onConfirm: (groupId: string, worksheetId: string) => void;

    /** Callback invoked when the user changes the selected group. */
    onGroupChange?: (groupId: string) => void;

    /** Callback to close the modal. */
    onHide: () => void;

    /** Whether the modal is currently visible. */
    show: boolean;

    /** Available worksheet options for the worksheet dropdown (scoped to the selected group). */
    worksheetOptions?: {
        /** The option identifier. */
        value: string;
        /** The option display text. */
        title: string;
    }[];
};

/**
 * Modal dialog for adding a worksheet to a submitted techline case. Presents two cascading
 * dropdowns: group and worksheet. The worksheet dropdown resets when the group changes.
 *
 * @param props - {@link AddWorksheetModalProps}
 * @returns the add worksheet modal JSX
 */
export function AddWorksheetModal({
    show,
    onHide,
    onConfirm,
    onGroupChange,
    groupOptions = [],
    worksheetOptions = [],
    isLoading = false,
}: AddWorksheetModalProps) {
    const [selectedGroup, setSelectedGroup] = useState<string>('');
    const [selectedWorksheet, setSelectedWorksheet] = useState<string>('');

    // Handlers

    /**
     * Handle closing the modal. Resets local selections and invokes the parent's onHide callback.
     */
    const handleClose = useCallback(() => {
        setSelectedGroup('');
        setSelectedWorksheet('');
        onHide();
    }, [onHide]);

    /**
     * Handle the confirm button press. Validates that both group and worksheet are selected,
     * then invokes the parent callback and closes the modal.
     */
    const handleConfirmButtonClick = useCallback(() => {
        if (selectedGroup && selectedWorksheet) {
            onConfirm(selectedGroup, selectedWorksheet);
            handleClose();
        }
    }, [handleClose, onConfirm, selectedGroup, selectedWorksheet]);

    /**
     * Handle selecting a group from the dropdown. Resets the worksheet selection
     * and notifies the parent about the group change.
     *
     * @param event - the native select change event
     */
    const handleGroupSelectChange = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const value = event.currentTarget.value;
            setSelectedGroup(value);

            // Reset worksheet selection when group changes
            setSelectedWorksheet('');

            // Notify parent component about group change
            onGroupChange?.(value);
        },
        [onGroupChange]
    );

    /**
     * Handle selecting a worksheet from the dropdown.
     *
     * @param event - the native select change event
     */
    const handleWorksheetSelectChange = useCallback((event: React.FormEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        setSelectedWorksheet(value);
    }, []);

    // Derived state

    /** Whether the confirm button should be disabled. */
    const isConfirmDisabled = !selectedGroup || !selectedWorksheet;

    return (
        <Modal backdrop="static" onHide={handleClose} show={show}>
            <Modal.Header closeButton>
                <Modal.Title>Add worksheet</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form.Group>
                    <div className="mb-4">
                        <Label required>Group</Label>
                        <Select
                            disabled={isLoading || groupOptions.length === 0}
                            onChange={handleGroupSelectChange}
                            options={groupOptions}
                            value={selectedGroup}
                        />
                    </div>

                    <div className="mb-4">
                        <Label required>Worksheet</Label>
                        <Select
                            disabled={isLoading || !selectedGroup || worksheetOptions.length === 0}
                            onChange={handleWorksheetSelectChange}
                            options={worksheetOptions}
                            value={selectedWorksheet}
                        />
                    </div>
                </Form.Group>
            </Modal.Body>

            <Modal.Footer>
                <Button onPress={handleClose} variant="link">
                    <Typography color="blueOne">Cancel</Typography>
                </Button>
                <Button
                    disabled={isConfirmDisabled || isLoading}
                    onPress={handleConfirmButtonClick}
                    style={confirmButtonStyle}
                    variant="primary"
                >
                    Add
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
